/**
 * HCT color conversion adapted from Material Color Utilities.
 *
 * Copyright 2021 Google LLC
 * Licensed under the Apache License, Version 2.0:
 * https://www.apache.org/licenses/LICENSE-2.0
 */

type Vector = [number, number, number];
type Matrix = [Vector, Vector, Vector];

export interface HctColor {
  hue: number;
  chroma: number;
  tone: number;
  argb: number;
}

export interface TonalPalette {
  hue: number;
  chroma: number;
  tone: (tone: number) => number;
}

interface ViewingConditions {
  n: number;
  aw: number;
  nbb: number;
  ncb: number;
  c: number;
  nc: number;
  rgbD: Vector;
  fl: number;
  fLRoot: number;
  z: number;
}

const SRGB_TO_XYZ: Matrix = [
  [0.41233895, 0.35762064, 0.18051042],
  [0.2126, 0.7152, 0.0722],
  [0.01932141, 0.11916382, 0.95034478],
];

const SCALED_DISCOUNT_FROM_LINRGB: Matrix = [
  [0.001200833568784504, 0.002389694492170889, 0.0002795742885861124],
  [0.0005891086651375999, 0.0029785502573438758, 0.0003270666104008398],
  [0.00010146692491640572, 0.0005364214359186694, 0.0032979401770712076],
];

const LINRGB_FROM_SCALED_DISCOUNT: Matrix = [
  [1373.2198709594231, -1100.4251190754821, -7.278681089101213],
  [-271.815969077903, 559.6580465940733, -32.46047482791194],
  [1.9622899599665666, -57.173814538844006, 308.7233197812385],
];

const Y_FROM_LINRGB: Vector = [0.2126, 0.7152, 0.0722];
const WHITE_POINT_D65: Vector = [95.047, 100, 108.883];

function signum(value: number): number {
  if (value < 0) return -1;
  if (value === 0) return 0;
  return 1;
}

function lerp(start: number, stop: number, amount: number): number {
  return (1 - amount) * start + amount * stop;
}

function clampInt(min: number, max: number, value: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function sanitizeDegrees(degrees: number): number {
  const sanitized = degrees % 360;
  return sanitized < 0 ? sanitized + 360 : sanitized;
}

function matrixMultiply(row: Vector, matrix: Matrix): Vector {
  return [
    row[0] * matrix[0][0] + row[1] * matrix[0][1] + row[2] * matrix[0][2],
    row[0] * matrix[1][0] + row[1] * matrix[1][1] + row[2] * matrix[1][2],
    row[0] * matrix[2][0] + row[1] * matrix[2][1] + row[2] * matrix[2][2],
  ];
}

function argbFromRgb(red: number, green: number, blue: number): number {
  const channels = Uint8Array.of(red, green, blue);
  return 0xff000000 + channels[0] * 0x10000 + channels[1] * 0x100 + channels[2];
}

function redFromArgb(argb: number): number {
  return Math.floor(argb / 0x10000) % 0x100;
}

function greenFromArgb(argb: number): number {
  return Math.floor(argb / 0x100) % 0x100;
}

function blueFromArgb(argb: number): number {
  return argb % 0x100;
}

function linearized(component: number): number {
  const normalized = component / 255;
  if (normalized <= 0.040449936) return (normalized / 12.92) * 100;
  return (((normalized + 0.055) / 1.055) ** 2.4) * 100;
}

function delinearized(component: number): number {
  const normalized = component / 100;
  const converted = normalized <= 0.0031308
    ? normalized * 12.92
    : 1.055 * (normalized ** (1 / 2.4)) - 0.055;

  return clampInt(0, 255, Math.round(converted * 255));
}

function argbFromLinrgb(linrgb: Vector): number {
  return argbFromRgb(
    delinearized(linrgb[0]),
    delinearized(linrgb[1]),
    delinearized(linrgb[2]),
  );
}

function labF(value: number): number {
  const e = 216 / 24389;
  const kappa = 24389 / 27;
  return value > e ? value ** (1 / 3) : (kappa * value + 16) / 116;
}

function labInvf(value: number): number {
  const e = 216 / 24389;
  const kappa = 24389 / 27;
  const cubed = value ** 3;
  return cubed > e ? cubed : (116 * value - 16) / kappa;
}

function yFromLstar(lstar: number): number {
  return 100 * labInvf((lstar + 16) / 116);
}

function argbFromLstar(lstar: number): number {
  const component = delinearized(yFromLstar(lstar));
  return argbFromRgb(component, component, component);
}

function lstarFromArgb(argb: number): number {
  const xyz = matrixMultiply([
    linearized(redFromArgb(argb)),
    linearized(greenFromArgb(argb)),
    linearized(blueFromArgb(argb)),
  ], SRGB_TO_XYZ);

  return 116 * labF(xyz[1] / 100) - 16;
}

function makeViewingConditions(): ViewingConditions {
  const [x, y, z] = WHITE_POINT_D65;
  const rW = x * 0.401288 + y * 0.650173 + z * -0.051461;
  const gW = x * -0.250268 + y * 1.204414 + z * 0.045854;
  const bW = x * -0.002079 + y * 0.048952 + z * 0.953127;
  const adaptingLuminance = ((200 / Math.PI) * yFromLstar(50)) / 100;
  const f = 1;
  const c = lerp(0.59, 0.69, (f - 0.9) * 10);
  const d = Math.min(1, Math.max(0, f * (1 - (1 / 3.6) * Math.exp((-adaptingLuminance - 42) / 92))));
  const nc = f;
  const rgbD: Vector = [
    d * (100 / rW) + 1 - d,
    d * (100 / gW) + 1 - d,
    d * (100 / bW) + 1 - d,
  ];
  const k = 1 / (5 * adaptingLuminance + 1);
  const k4 = k ** 4;
  const fl = k4 * adaptingLuminance
    + 0.1 * ((1 - k4) ** 2) * Math.cbrt(5 * adaptingLuminance);
  const n = yFromLstar(50) / y;
  const zValue = 1.48 + Math.sqrt(n);
  const nbb = 0.725 / (n ** 0.2);
  const rgbAFactors: Vector = [
    ((fl * rgbD[0] * rW) / 100) ** 0.42,
    ((fl * rgbD[1] * gW) / 100) ** 0.42,
    ((fl * rgbD[2] * bW) / 100) ** 0.42,
  ];
  const rgbA: Vector = [
    (400 * rgbAFactors[0]) / (rgbAFactors[0] + 27.13),
    (400 * rgbAFactors[1]) / (rgbAFactors[1] + 27.13),
    (400 * rgbAFactors[2]) / (rgbAFactors[2] + 27.13),
  ];
  const aw = (2 * rgbA[0] + rgbA[1] + 0.05 * rgbA[2]) * nbb;

  return {
    n,
    aw,
    nbb,
    ncb: nbb,
    c,
    nc,
    rgbD,
    fl,
    fLRoot: fl ** 0.25,
    z: zValue,
  };
}

const VIEWING_CONDITIONS = makeViewingConditions();

function cam16FromInt(argb: number): { hue: number; chroma: number } {
  const redL = linearized(redFromArgb(argb));
  const greenL = linearized(greenFromArgb(argb));
  const blueL = linearized(blueFromArgb(argb));
  const x = 0.41233895 * redL + 0.35762064 * greenL + 0.18051042 * blueL;
  const y = 0.2126 * redL + 0.7152 * greenL + 0.0722 * blueL;
  const z = 0.01932141 * redL + 0.11916382 * greenL + 0.95034478 * blueL;
  const rC = 0.401288 * x + 0.650173 * y - 0.051461 * z;
  const gC = -0.250268 * x + 1.204414 * y + 0.045854 * z;
  const bC = -0.002079 * x + 0.048952 * y + 0.953127 * z;
  const rD = VIEWING_CONDITIONS.rgbD[0] * rC;
  const gD = VIEWING_CONDITIONS.rgbD[1] * gC;
  const bD = VIEWING_CONDITIONS.rgbD[2] * bC;
  const rAF = ((VIEWING_CONDITIONS.fl * Math.abs(rD)) / 100) ** 0.42;
  const gAF = ((VIEWING_CONDITIONS.fl * Math.abs(gD)) / 100) ** 0.42;
  const bAF = ((VIEWING_CONDITIONS.fl * Math.abs(bD)) / 100) ** 0.42;
  const rA = (signum(rD) * 400 * rAF) / (rAF + 27.13);
  const gA = (signum(gD) * 400 * gAF) / (gAF + 27.13);
  const bA = (signum(bD) * 400 * bAF) / (bAF + 27.13);
  const a = (11 * rA - 12 * gA + bA) / 11;
  const b = (rA + gA - 2 * bA) / 9;
  const u = (20 * rA + 20 * gA + 21 * bA) / 20;
  const p2 = (40 * rA + 20 * gA + bA) / 20;
  const hue = sanitizeDegrees((Math.atan2(b, a) * 180) / Math.PI);
  const huePrime = hue < 20.14 ? hue + 360 : hue;
  const eHue = 0.25 * (Math.cos((huePrime * Math.PI) / 180 + 2) + 3.8);
  const ac = p2 * VIEWING_CONDITIONS.nbb;
  const j = 100 * ((ac / VIEWING_CONDITIONS.aw) ** (VIEWING_CONDITIONS.c * VIEWING_CONDITIONS.z));
  const p1 = (50000 / 13) * eHue * VIEWING_CONDITIONS.nc * VIEWING_CONDITIONS.ncb;
  const t = (p1 * Math.sqrt(a * a + b * b)) / (u + 0.305);
  const alpha = (t ** 0.9) * ((1.64 - (0.29 ** VIEWING_CONDITIONS.n)) ** 0.73);

  return { hue, chroma: alpha * Math.sqrt(j / 100) };
}

function chromaticAdaptation(component: number): number {
  const af = Math.abs(component) ** 0.42;
  return (signum(component) * 400 * af) / (af + 27.13);
}

function hueOf(linrgb: Vector): number {
  const [rAInput, gAInput, bAInput] = matrixMultiply(linrgb, SCALED_DISCOUNT_FROM_LINRGB);
  const rA = chromaticAdaptation(rAInput);
  const gA = chromaticAdaptation(gAInput);
  const bA = chromaticAdaptation(bAInput);
  const a = (11 * rA - 12 * gA + bA) / 11;
  const b = (rA + gA - 2 * bA) / 9;
  return Math.atan2(b, a);
}

function sanitizeRadians(angle: number): number {
  return (angle + Math.PI * 8) % (Math.PI * 2);
}

function areInCyclicOrder(a: number, b: number, c: number): boolean {
  return sanitizeRadians(b - a) < sanitizeRadians(c - a);
}

function lerpPoint(source: Vector, amount: number, target: Vector): Vector {
  return [
    source[0] + (target[0] - source[0]) * amount,
    source[1] + (target[1] - source[1]) * amount,
    source[2] + (target[2] - source[2]) * amount,
  ];
}

function setCoordinate(source: Vector, coordinate: number, target: Vector, axis: number): Vector {
  return lerpPoint(source, (coordinate - source[axis]) / (target[axis] - source[axis]), target);
}

function isBounded(value: number): boolean {
  return value >= 0 && value <= 100;
}

function nthVertex(y: number, index: number): Vector {
  const [kR, kG, kB] = Y_FROM_LINRGB;
  const coordA = index % 4 <= 1 ? 0 : 100;
  const coordB = index % 2 === 0 ? 0 : 100;

  if (index < 4) {
    const r = (y - coordA * kG - coordB * kB) / kR;
    return isBounded(r) ? [r, coordA, coordB] : [-1, -1, -1];
  }
  if (index < 8) {
    const g = (y - coordB * kR - coordA * kB) / kG;
    return isBounded(g) ? [coordB, g, coordA] : [-1, -1, -1];
  }

  const b = (y - coordA * kR - coordB * kG) / kB;
  return isBounded(b) ? [coordA, coordB, b] : [-1, -1, -1];
}

function bisectToSegment(y: number, targetHue: number): [Vector, Vector] {
  let left: Vector = [-1, -1, -1];
  let right = left;
  let leftHue = 0;
  let rightHue = 0;
  let initialized = false;
  let uncut = true;

  for (let index = 0; index < 12; index += 1) {
    const mid = nthVertex(y, index);
    if (mid[0] < 0) continue;
    const midHue = hueOf(mid);

    if (!initialized) {
      left = mid;
      right = mid;
      leftHue = midHue;
      rightHue = midHue;
      initialized = true;
      continue;
    }

    if (uncut || areInCyclicOrder(leftHue, midHue, rightHue)) {
      uncut = false;
      if (areInCyclicOrder(leftHue, targetHue, midHue)) {
        right = mid;
        rightHue = midHue;
      } else {
        left = mid;
        leftHue = midHue;
      }
    }
  }

  return [left, right];
}

function trueDelinearized(component: number): number {
  const normalized = component / 100;
  const converted = normalized <= 0.0031308
    ? normalized * 12.92
    : 1.055 * (normalized ** (1 / 2.4)) - 0.055;
  return converted * 255;
}

const CRITICAL_PLANES = Array.from(
  { length: 255 },
  (_, index) => linearized(index + 0.5),
);

function bisectToLimit(y: number, targetHue: number): Vector {
  const segment = bisectToSegment(y, targetHue);
  let left = segment[0];
  let leftHue = hueOf(left);
  let right = segment[1];

  for (let axis = 0; axis < 3; axis += 1) {
    if (left[axis] === right[axis]) continue;
    let leftPlane: number;
    let rightPlane: number;

    if (left[axis] < right[axis]) {
      leftPlane = Math.floor(trueDelinearized(left[axis]) - 0.5);
      rightPlane = Math.ceil(trueDelinearized(right[axis]) - 0.5);
    } else {
      leftPlane = Math.ceil(trueDelinearized(left[axis]) - 0.5);
      rightPlane = Math.floor(trueDelinearized(right[axis]) - 0.5);
    }

    for (let iteration = 0; iteration < 8; iteration += 1) {
      if (Math.abs(rightPlane - leftPlane) <= 1) break;
      const midPlane = Math.floor((leftPlane + rightPlane) / 2);
      const mid = setCoordinate(left, CRITICAL_PLANES[midPlane], right, axis);
      const midHue = hueOf(mid);

      if (areInCyclicOrder(leftHue, targetHue, midHue)) {
        right = mid;
        rightPlane = midPlane;
      } else {
        left = mid;
        leftHue = midHue;
        leftPlane = midPlane;
      }
    }
  }

  return [
    (left[0] + right[0]) / 2,
    (left[1] + right[1]) / 2,
    (left[2] + right[2]) / 2,
  ];
}

function inverseChromaticAdaptation(adapted: number): number {
  const absolute = Math.abs(adapted);
  const base = Math.max(0, (27.13 * absolute) / (400 - absolute));
  return signum(adapted) * (base ** (1 / 0.42));
}

function findResultByJ(hueRadians: number, chroma: number, y: number): number {
  let j = Math.sqrt(y) * 11;
  const vc = VIEWING_CONDITIONS;
  const tInnerCoeff = 1 / ((1.64 - (0.29 ** vc.n)) ** 0.73);
  const eHue = 0.25 * (Math.cos(hueRadians + 2) + 3.8);
  const p1 = eHue * (50000 / 13) * vc.nc * vc.ncb;
  const hSin = Math.sin(hueRadians);
  const hCos = Math.cos(hueRadians);

  for (let iteration = 0; iteration < 5; iteration += 1) {
    const jNormalized = j / 100;
    const alpha = chroma === 0 || j === 0 ? 0 : chroma / Math.sqrt(jNormalized);
    const t = ((alpha * tInnerCoeff) ** (1 / 0.9));
    const ac = vc.aw * (jNormalized ** (1 / vc.c / vc.z));
    const p2 = ac / vc.nbb;
    const gamma = (23 * (p2 + 0.305) * t)
      / (23 * p1 + 11 * t * hCos + 108 * t * hSin);
    const a = gamma * hCos;
    const b = gamma * hSin;
    const rA = (460 * p2 + 451 * a + 288 * b) / 1403;
    const gA = (460 * p2 - 891 * a - 261 * b) / 1403;
    const bA = (460 * p2 - 220 * a - 6300 * b) / 1403;
    const linrgb = matrixMultiply([
      inverseChromaticAdaptation(rA),
      inverseChromaticAdaptation(gA),
      inverseChromaticAdaptation(bA),
    ], LINRGB_FROM_SCALED_DISCOUNT);

    if (linrgb[0] < 0 || linrgb[1] < 0 || linrgb[2] < 0) return 0;
    const resolvedY = Y_FROM_LINRGB[0] * linrgb[0]
      + Y_FROM_LINRGB[1] * linrgb[1]
      + Y_FROM_LINRGB[2] * linrgb[2];
    if (resolvedY <= 0) return 0;

    if (iteration === 4 || Math.abs(resolvedY - y) < 0.002) {
      if (linrgb[0] > 100.01 || linrgb[1] > 100.01 || linrgb[2] > 100.01) return 0;
      return argbFromLinrgb(linrgb);
    }

    j -= ((resolvedY - y) * j) / (2 * resolvedY);
  }

  return 0;
}

function solveToInt(hue: number, chroma: number, tone: number): number {
  if (chroma < 0.0001 || tone < 0.0001 || tone > 99.9999) return argbFromLstar(tone);
  const hueRadians = (sanitizeDegrees(hue) / 180) * Math.PI;
  const y = yFromLstar(tone);
  const exact = findResultByJ(hueRadians, chroma, y);
  return exact || argbFromLinrgb(bisectToLimit(y, hueRadians));
}

export function argbFromHex(hexValue: string): number {
  const hex = hexValue.replace('#', '');
  const isThree = hex.length === 3;
  const isSix = hex.length === 6;
  const isEight = hex.length === 8;
  if (!isThree && !isSix && !isEight) throw new Error(`unexpected hex ${hex}`);

  let red = 0;
  let green = 0;
  let blue = 0;
  if (isThree) {
    red = parseInt(hex.slice(0, 1).repeat(2), 16);
    green = parseInt(hex.slice(1, 2).repeat(2), 16);
    blue = parseInt(hex.slice(2, 3).repeat(2), 16);
  } else if (isSix) {
    red = parseInt(hex.slice(0, 2), 16);
    green = parseInt(hex.slice(2, 4), 16);
    blue = parseInt(hex.slice(4, 6), 16);
  } else {
    red = parseInt(hex.slice(2, 4), 16);
    green = parseInt(hex.slice(4, 6), 16);
    blue = parseInt(hex.slice(6, 8), 16);
  }

  return argbFromRgb(red, green, blue);
}

export function hexFromArgb(argb: number): string {
  const channels = [redFromArgb(argb), greenFromArgb(argb), blueFromArgb(argb)];
  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

export function hctFromInt(argb: number): HctColor {
  const { hue, chroma } = cam16FromInt(argb);
  return { hue, chroma, tone: lstarFromArgb(argb), argb };
}

export function hctFrom(hue: number, chroma: number, tone: number): HctColor {
  return hctFromInt(solveToInt(hue, chroma, tone));
}

function averageArgb(first: number, second: number): number {
  return argbFromRgb(
    Math.round((redFromArgb(first) + redFromArgb(second)) / 2),
    Math.round((greenFromArgb(first) + greenFromArgb(second)) / 2),
    Math.round((blueFromArgb(first) + blueFromArgb(second)) / 2),
  );
}

export function tonalPaletteFromInt(argb: number): TonalPalette {
  const seed = hctFromInt(argb);
  const cache = new Map<number, number>();

  function tone(toneValue: number): number {
    const cached = cache.get(toneValue);
    if (cached !== undefined) return cached;

    const resolved = toneValue === 99 && seed.hue >= 105 && seed.hue < 125
      ? averageArgb(tone(98), tone(100))
      : solveToInt(seed.hue, seed.chroma, toneValue);
    cache.set(toneValue, resolved);
    return resolved;
  }

  return { hue: seed.hue, chroma: seed.chroma, tone };
}
